import { FormControl, FormLabel, SimpleSelect, Stack, TextField, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { isDurationString } from '@chronosphereio/core';
import { InformationOutline as InfoIcon } from 'mdi-material-ui';
import { useEffect, useState } from 'react';
import {
  DEFAULT_QUERY_RESOLUTION,
  isValidResolutionLevel,
  parseTemplateVariables,
  QueryOptionsDefinition,
  QueryResolutionLevel,
} from '@/core';

const RESOLUTION_HELP_TEXT = `Controls the 'step' used in panel queries by setting the maximum data points per series. Low resolution prioritizes performance over accuracy, while High resolution provides more precise results with the potential for degraded performance.`;

const RESOLUTION_OPTIONS: Array<{ key: QueryResolutionLevel; label: string }> = [
  { key: 'Low', label: 'Low' },
  { key: 'Medium', label: 'Medium (Default)' },
  { key: 'High', label: 'High' },
];

export interface ResolutionEditorProps {
  resolution?: QueryOptionsDefinition['resolution'];
  onResolutionChange: (newResolution: QueryOptionsDefinition['resolution']) => void;
}

export function ResolutionEditor({ resolution = DEFAULT_QUERY_RESOLUTION, onResolutionChange }: ResolutionEditorProps) {
  const [customResolutionText, setCustomResolutionText] = useState('');
  const [isCustomResolution, setIsCustomResolution] = useState(
    !isValidResolutionLevel(resolution) || resolution === undefined
  );

  const hasVariables = customResolutionText && parseTemplateVariables(customResolutionText).length > 0;

  // Sync custom input when not a resolution preset (ex: 'Low', 'Medium', 'High')
  useEffect(() => {
    if (!isValidResolutionLevel(resolution)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCustomResolutionText(resolution);
    }
  }, [resolution]);

  const handleSubmitCustomResolution = () => {
    if (isDurationString(customResolutionText) || hasVariables) {
      onResolutionChange(customResolutionText);
    } else {
      onResolutionChange(undefined);
    }
  };

  return (
    <FormControl fullWidth>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <FormLabel>
            <Typography variant="body1" color="text.primary">
              Resolution
            </Typography>
          </FormLabel>
          <Tooltip Content={RESOLUTION_HELP_TEXT}>
            <InfoIcon aria-hidden={false} fontSize="inherit" color="secondary" />
          </Tooltip>
        </Stack>
        <SimpleSelect
          options={[...RESOLUTION_OPTIONS, { key: 'Custom', label: 'Custom' }]}
          value={isCustomResolution ? 'Custom' : resolution}
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'Custom') {
              setIsCustomResolution(true);
              setCustomResolutionText('');
            } else if (isValidResolutionLevel(value)) {
              setIsCustomResolution(false);
              onResolutionChange(value);
            }
          }}
        />
      </Stack>
      {isCustomResolution && (
        <FormControl fullWidth sx={{ mt: 1 }}>
          <Stack direction="row" alignItems="start" justifyContent="space-between">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
              // Make sure the height of the label is the same as the input
              sx={{ height: '32px', lineHeight: '32px' }}
            >
              <FormLabel htmlFor="custom-resolution-input">
                <Typography color="text.primary">Custom resolution</Typography>
              </FormLabel>
            </Stack>
            <TextField
              id="custom-resolution-input"
              placeholder="Ex. 30s, 5m"
              value={customResolutionText}
              onChange={(e) => setCustomResolutionText(e.target.value)}
              onBlur={handleSubmitCustomResolution}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmitCustomResolution();
                }
              }}
              error={!!customResolutionText && !isDurationString(customResolutionText) && !hasVariables}
              helperText={
                customResolutionText && !isDurationString(customResolutionText) && !hasVariables
                  ? 'Invalid value. Use: 30s, 5m, 1h, etc.'
                  : ''
              }
            />
          </Stack>
        </FormControl>
      )}
    </FormControl>
  );
}
